import { ROUTES } from 'Consts/routes';
import Button, { BUTTON_TYPEFACES } from 'UI/Elements/Button';
import Icon, { IconNames } from 'UI/Elements/Icon';
import { IconShape } from 'UI/Elements/Icon/icons';
import cn from 'classnames';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { groupEmployeeList } from 'Pages/Zones/utils';
import { useAuth0 } from '@auth0/auth0-react';
import { Nullable } from 'Consts/types';
import * as actions from 'State/actions';
import useCustomer from 'State/hooks/useCustomer';
import useDevices from 'State/hooks/useDevices';
import useEmployees from 'State/hooks/useEmployees';
import useNetworkAccess from 'State/hooks/useNetworkAccess';
import useSecurityPolicy from 'State/hooks/useSecurityPolicy';
import * as selectors from 'State/selectors';
import { AppDispatch } from 'State/store';
import Alert from 'UI/Components/Alert';
import Menu from 'UI/Components/Menu';
import { BUTTON_THEMES } from 'UI/Elements/Button';
import AlertModal from 'UI/Elements/Modals/Alerts';
import PartnerImg from 'UI/Elements/Partner Logo';
import { Body3 } from 'UI/Elements/Typography';
import V2Menu, { MenuItemProps } from 'UI/Molecules/Menu';
import useCspTranslationNamespace from 'Utils/hooks/useCspTranslationNamespace';
import { MenuOpenTriggerEventType } from 'Utils/hooks/useFocusFirstInteractable';
import { getConfigurationFromDomain } from 'subDomainConfiguration';
import { useTrackEvent } from 'trackingAnalytics/hooks/useTrackEvent';
import { MixPanelEvents } from 'trackingAnalytics/mixPanelEvents';
import AutoComplete, {
  AutoCompleteParams,
} from '../../Components/AutoComplete/AutoComplete';
import { AutoCompleteResultsItem } from '../../Components/AutoComplete/AutoCompleteResults';
import styles from './style.module.css';
import useCustomerSupportConfigurations from 'State/hooks/useCustomerSupportConfigurations';
import useLocationCapabilities from 'State/hooks/useLocationCapabilities';
import useEmployeeLoginDatabase from 'State/hooks/useEmployeeLoginDatabase';
import { useTrapFocus } from 'Utils/accessibility/useTrapFocus';

import { memo } from 'react';

const MAX_NOTIFICATIONS_ALLOWED = 99;
const BELL_NAMESPACE = 'bell';

type MenuElementProps = {
  onClick: React.MouseEventHandler;
};

type BrandingProps = {
  partnerId?: Nullable<string>;
} & MenuElementProps;

type BadgeProps = {
  count: number;
};

type NotificationsProps = {
  noDevicesCount?: number;
  noPrimaryDeviceCount?: number;
  unapprovedSecureZoneDevicesCount?: number;
  unapprovedEmployeeZoneDevicesCount?: number;
  notifyBusinessProfileIsIncomplete?: boolean;
  notifyAppMonitoringIsDisabled?: boolean;
  notifyEmployeeLoginIsDisabled?: boolean;
};

type UserProps = {
  handleSignOut: () => void;
};

const Hamburger: FunctionComponent<MenuElementProps> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <Icon
      name={IconNames.Hamburger}
      shape={IconShape.circle}
      className={styles.hideForNarrowScreens}
      tooltipLabel={t('common.hideNav')}
      onClick={onClick}
    />
  );
};

export const Branding: FunctionComponent<BrandingProps> = ({
  partnerId = undefined,
  onClick,
}) => {
  const environment = getConfigurationFromDomain();

  return environment.id === BELL_NAMESPACE ? (
    <div
      role="button"
      tabIndex={0}
      aria-label="Go to home"
      className={styles.logoLine}
      onClick={onClick}
      data-testid="bell-logo-icon"
    >
      <Icon
        className={styles.logo}
        name={IconNames.BellCompanyBrandingLogo}
        shape={IconShape.rectangle}
      />
    </div>
  ) : (
    <div
      role="button"
      tabIndex={0}
      aria-label="Go to home"
      className={styles.logoLine}
      onClick={onClick}
      data-testid="plume-logo-icon"
    >
      <PartnerImg className={styles.partnerLogo} partnerId={partnerId} />
      <Icon
        className={styles.logo}
        name={IconNames.Logo}
        shape={IconShape.rectangle}
      />
    </div>
  );
};

const Badge: FunctionComponent<BadgeProps> = ({ count }) => {
  const hasMoreThanLimit = !!count && count > MAX_NOTIFICATIONS_ALLOWED;
  const badgeClassNames = cn(styles.badge, {
    [styles.badgeNarrowed]: hasMoreThanLimit,
  });
  const badgeCopy = hasMoreThanLimit ? `${MAX_NOTIFICATIONS_ALLOWED}+` : count;

  return !!count ? (
    <Body3 className={badgeClassNames}>{badgeCopy}</Body3>
  ) : null;
};

const Notifications: FunctionComponent<NotificationsProps> = ({
  noDevicesCount = 0,
  noPrimaryDeviceCount = 0,
  unapprovedSecureZoneDevicesCount = 0,
  unapprovedEmployeeZoneDevicesCount = 0,
  notifyBusinessProfileIsIncomplete = false,
  notifyAppMonitoringIsDisabled = false,
  notifyEmployeeLoginIsDisabled = false,
}) => {
  const { t } = useTranslation();
  const { handleTriggerEvent } = useTrapFocus();
  const count =
    (notifyAppMonitoringIsDisabled ? 1 : 0) +
    (notifyBusinessProfileIsIncomplete ? 1 : 0) +
    (notifyEmployeeLoginIsDisabled ? 1 : 0) +
    (noDevicesCount ? 1 : 0) +
    (noPrimaryDeviceCount ? 1 : 0) +
    (unapprovedSecureZoneDevicesCount ? 1 : 0) +
    (unapprovedEmployeeZoneDevicesCount ? 1 : 0);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownMenuOpenTrigger, setDropdownMenuOpenTrigger] =
    useState<MenuOpenTriggerEventType>();
  const parentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const namespace = useCspTranslationNamespace();
  const notificationsMenuEmpty: MenuItemProps[] = [
    {
      label: t('common.notificationsTab.noNewNotifications.title'),
      className: cn(styles.notificationsMenuEmpty),
    },
  ];

  const noDevicesNotifications = () => {
    if ((noDevicesCount ?? 0) > 0) {
      return [
        {
          label:
            noDevicesCount === 1
              ? t('common.notificationsTab.noDevices.titleOne')
              : t('common.notificationsTab.noDevices.title', {
                noDevice: noDevicesCount,
              }),
          subtitle: t('common.notificationsTab.noDevices.subtitle', {
            ns: namespace,
          }),
          onClick: () => {
            navigate(ROUTES.zones.employee.index);
          },
        },
      ];
    } else {
      return [];
    }
  };

  const primaryDeviceNotifications = () => {
    if ((noPrimaryDeviceCount ?? 0) > 0) {
      return [
        {
          label:
            noPrimaryDeviceCount === 1
              ? t('common.notificationsTab.noPrimaryDevice.titleOne')
              : t('common.notificationsTab.noPrimaryDevice.title', {
                noPrimaryDevice: noPrimaryDeviceCount,
              }),
          subtitle: t('common.notificationsTab.noPrimaryDevice.subtitle', {
            ns: namespace,
          }),
          onClick: () => {
            navigate(ROUTES.zones.employee.index);
          },
        },
      ];
    } else {
      return [];
    }
  };

  const unapprovedSecureZoneDeviceNotifications = () => {
    if ((unapprovedSecureZoneDevicesCount ?? 0) > 0) {
      return [
        {
          label:
            unapprovedSecureZoneDevicesCount === 1
              ? t(
                'common.notificationsTab.unapprovedSecureZoneDevices.titleOne'
              )
              : t('common.notificationsTab.unapprovedSecureZoneDevices.title', {
                unapprovedSecureZoneDevices: unapprovedSecureZoneDevicesCount,
              }),
          subtitle: t(
            'common.notificationsTab.unapprovedSecureZoneDevices.subtitle'
          ),
          onClick: () => {
            navigate(ROUTES.zones.secure.unapproved);
          },
        },
      ];
    } else {
      return [];
    }
  };

  const unapprovedEmployeeZoneDeviceNotifications = () => {
    if ((unapprovedEmployeeZoneDevicesCount ?? 0) > 0) {
      return [
        {
          label:
            unapprovedEmployeeZoneDevicesCount === 1
              ? t(
                'common.notificationsTab.unapprovedEmployeeZoneDevices.titleOne'
              )
              : t(
                'common.notificationsTab.unapprovedEmployeeZoneDevices.title',
                {
                  unapprovedEmployeeZoneDevices:
                    unapprovedEmployeeZoneDevicesCount,
                }
              ),
          subtitle: t(
            'common.notificationsTab.unapprovedEmployeeZoneDevices.subtitle'
          ),
          onClick: () => {
            navigate(ROUTES.zones.employee.unapproved);
          },
        },
      ];
    } else {
      return [];
    }
  };

  const incompleteBusinessProfileNotifications = () => {
    return notifyBusinessProfileIsIncomplete
      ? [
        {
          label: t(
            'common.notificationsTab.businessInfoProfileNotComplete.title'
          ),
          subtitle: t(
            'common.notificationsTab.businessInfoProfileNotComplete.subtitle'
          ),
          onClick: () => {
            navigate(ROUTES.settings.businessInfo);
          },
        },
      ]
      : [];
  };

  const disabledAppMonitoringNotifications = () => {
    return notifyAppMonitoringIsDisabled
      ? [
        {
          label: t('common.notificationsTab.appMonitoringEnablePrompt.title'),
          subtitle: t(
            'common.notificationsTab.appMonitoringEnablePrompt.subtitle'
          ),
          onClick: () => {
            navigate(ROUTES.settings.shield);
          },
        },
      ]
      : [];
  };

  const disabledEmployeeLoginNotifications = () => {
    return notifyEmployeeLoginIsDisabled
      ? [
        {
          label: t('common.notificationsTab.employeeLoginIsDisabled.title'),
          subtitle: t(
            'common.notificationsTab.employeeLoginIsDisabled.subtitle'
          ),
          onClick: () => {
            navigate(ROUTES.settings.shield);
          },
        },
      ]
      : [];
  };

  const notificationsMenu: MenuItemProps[] =
    count === 0
      ? [...notificationsMenuEmpty]
      : [
        ...disabledAppMonitoringNotifications(),
        ...incompleteBusinessProfileNotifications(),
        ...disabledEmployeeLoginNotifications(),
        ...noDevicesNotifications(),
        ...primaryDeviceNotifications(),
        ...unapprovedSecureZoneDeviceNotifications(),
        ...unapprovedEmployeeZoneDeviceNotifications(),
      ]
        .flatMap((ele) => [ele, { isDivider: true }])
        .slice(0, -1);

  const handleIconClick = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    handleTriggerEvent(ev);
    setDropdownOpen(!dropdownOpen);
    setDropdownMenuOpenTrigger(ev.type as MenuOpenTriggerEventType);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
  };

  return (
    <div
      className={styles.notifications}
      ref={parentRef}
      data-testid="notifications"
    >
      <Icon
        name={IconNames.Bell}
        shape={IconShape.circle}
        tooltipLabel={t('common.viewNotifications')}
        onClick={handleIconClick}
      />
      <Badge count={count} />
      <V2Menu
        isOpen={dropdownOpen}
        items={notificationsMenu}
        parent={parentRef.current}
        onClose={closeDropdown}
        menuOpenTrigger={dropdownMenuOpenTrigger}
      />
    </div>
  );
};

const User: FunctionComponent<UserProps> = ({ handleSignOut }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpenTrigger, setMenuOpenTrigger] =
    useState<MenuOpenTriggerEventType>();
  const parentRef = useRef<HTMLDivElement>(null);
  const customerData = useCustomer();
  const email = customerData?.data?.email || ' ';

  const userMenu: MenuItemProps[] = [
    {
      label: 'Sign out',
      theme: 'danger',
      iconName: IconNames.SignOut,
      onClick: handleSignOut,
    },
  ];

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setDropdownOpen(!dropdownOpen);
    setMenuOpenTrigger(e.type as MenuOpenTriggerEventType);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
  };

  return (
    <div ref={parentRef} data-testid="user">
      <Button
        className={styles.userButton}
        label={email[0].toUpperCase()}
        typeface={BUTTON_TYPEFACES.unstyled}
        onClick={handleIconClick}
      />

      <Menu
        className={styles.userMenu}
        isOpen={dropdownOpen}
        items={userMenu}
        parent={parentRef.current}
        onClose={closeDropdown}
        openTriggerEventType={menuOpenTrigger}
      />
    </div>
  );
};

type TopBarBaseProps = JSX.IntrinsicElements['div'] & {
  showSearchAndNotifications?: boolean;
};

type TopBarUIProps = {
  hamburgerOnClick: React.MouseEventHandler;
  logoOnClick: React.MouseEventHandler;
  noDevicesCount: number;
  noPrimaryDeviceCount: number;
  unapprovedSecureZoneDevicesCount: number;
  unapprovedEmployeeZoneDevicesCount: number;
  withDebugMessage?: boolean;
  partnerId?: Nullable<string>;
  showSearchAndNotifications?: boolean;
  notifyBusinessProfileIsIncomplete?: boolean;
  notifyAppMonitoringIsDisabled?: boolean;
  notifyEmployeeLoginIsDisabled?: boolean;
} & TopBarBaseProps;

enum AutoCompleteResultTypes {
  Employee = 'employee',
  Device = 'device',
  Location = 'location',
}

const mapAutoCompleteTypeToUrlTemplate: Record<
  AutoCompleteResultTypes,
  string
> = {
  [AutoCompleteResultTypes.Employee]: '/employee/:id',
  [AutoCompleteResultTypes.Device]: '/device/:id',
  [AutoCompleteResultTypes.Location]: ROUTES.home.index,
};

export const TopBarUI: FunctionComponent<TopBarUIProps> = ({
  className,
  hamburgerOnClick,
  logoOnClick,
  noDevicesCount,
  noPrimaryDeviceCount,
  unapprovedSecureZoneDevicesCount,
  unapprovedEmployeeZoneDevicesCount,
  withDebugMessage,
  partnerId = undefined,
  showSearchAndNotifications = true,
  notifyBusinessProfileIsIncomplete = false,
  notifyAppMonitoringIsDisabled = false,
  notifyEmployeeLoginIsDisabled = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, logout } = useAuth0();
  const trackEvent = useTrackEvent();
  const { t } = useTranslation();
  const namespace = useCspTranslationNamespace();
  const navigate = useNavigate();
  const employees = useSelector(selectors.employees);
  const devices = useSelector(selectors.zones.devices);
  const locations = useSelector(selectors.locations.mergedLocations);
  const [openAlert, setOpenAlert] = useState<'signout' | null>(null);

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(null);
  }, []);

  const handleOpenSignoutAlert = useCallback(() => setOpenAlert('signout'), []);

  const onSearch = (query: string, params: AutoCompleteParams) => {
    const employeesData = (employees.data || [])
      .map((d) => {
        return {
          id: d.id,
          label: d.nickname,
          type: AutoCompleteResultTypes.Employee,
        };
      })
      .filter((d) => d.label.toLowerCase().includes(query.toLowerCase()));
    const devicesData = (devices.data || [])
      .map((d) => {
        return {
          id: d.mac,
          label: d.nickname || d.name || d.mac,
          type: AutoCompleteResultTypes.Device,
        };
      })
      .filter((d) => {
        return d.label.toLowerCase().includes(query.toLowerCase());
      });
    const devicesDataIPAddress = (devices.data || [])
      .map((d) => {
        return {
          id: d.mac,
          label: d.ip || '',
          type: AutoCompleteResultTypes.Device,
          searchItem: d.ip,
        };
      })
      .filter((d) => {
        return d.searchItem?.includes(query) && query.length > 2;
      });
    const locationsData = locations
      .map((location) => {
        return {
          id: location.id,
          label: location.name || '',
          type: AutoCompleteResultTypes.Location,
        };
      })
      .filter((location) =>
        location.label.toLowerCase().includes(query.toLowerCase())
      );

    return Promise.resolve([
      ...employeesData,
      ...devicesData,
      ...devicesDataIPAddress,
      ...locationsData,
    ]);
  };

  const onAutoCompleteItemClick = (item: AutoCompleteResultsItem) => {
    if (item.type === AutoCompleteResultTypes.Location) {
      navigate(ROUTES.home.index);
      dispatch(actions.locations.switchLocation({ locationId: item.id }));
      return;
    }

    const urlTemplate =
      mapAutoCompleteTypeToUrlTemplate[item.type as AutoCompleteResultTypes];
    const url = urlTemplate.replace(':id', item.id);
    navigate(url);
  };

  const handleSignOut = useCallback(() => {
    dispatch(actions.auth.logout());

    if (isAuthenticated) {
      logout({
        logoutParams: {
          returnTo: window.location.origin + '/onboarding/global-auth',
        },
      });
    }

    trackEvent({
      eventName: MixPanelEvents.LOGOUT,
    });
  }, [dispatch]);

  return (
    <div className={cn(styles.topBar, className)}>
      <div className={styles.topBarLeft}>
        <Hamburger onClick={hamburgerOnClick} />
        <Branding partnerId={partnerId} onClick={logoOnClick} />
      </div>

      <div className={styles.topBarRight}>
        {showSearchAndNotifications && (
          <>
            <AutoComplete
              onSearch={onSearch}
              configuration={{ maxHeight: 248 }}
              availableFilters={[
                {
                  name: 'employee',
                  label: 'Employees',
                },
                {
                  name: 'device',
                  label: 'Devices',
                },
                {
                  name: 'location',
                  label: t('common.locations'),
                },
              ]}
              onItemClick={onAutoCompleteItemClick}
              autoCompleteInputId="autoComplete-input"
            />
            {withDebugMessage && (
              <div className={styles.debugMessage}>Debug mode</div>
            )}
            <Notifications
              noDevicesCount={noDevicesCount}
              noPrimaryDeviceCount={noPrimaryDeviceCount}
              unapprovedSecureZoneDevicesCount={
                unapprovedSecureZoneDevicesCount
              }
              unapprovedEmployeeZoneDevicesCount={
                unapprovedEmployeeZoneDevicesCount
              }
              notifyBusinessProfileIsIncomplete={
                notifyBusinessProfileIsIncomplete
              }
              notifyAppMonitoringIsDisabled={notifyAppMonitoringIsDisabled}
              notifyEmployeeLoginIsDisabled={notifyEmployeeLoginIsDisabled}
            />
          </>
        )}
        <User handleSignOut={handleOpenSignoutAlert} />
      </div>

      <AlertModal isOpen={openAlert === 'signout'} onClose={handleCloseAlert}>
        <Alert
          topProps={{
            label: t('settings.signingOut', { ns: namespace }),
            paragraph: t('settings.signingOutText', { ns: namespace }),
            className: styles.modalBody,
          }}
          bottomProps={{
            button1Props: {
              label: t('common.yes'),
              onClick: handleSignOut,
            },
            button2Props: {
              label: t('common.cancel'),
              onClick: handleCloseAlert,
              theme: BUTTON_THEMES.white,
            },
          }}
        />
      </AlertModal>
    </div>
  );
};

const TopBar: FunctionComponent<TopBarBaseProps> = ({
  className,
  showSearchAndNotifications = true,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const activeLocation = useSelector(selectors.locations.activeLocation);

  const debugEnabled = useSelector(selectors.debug);
  const navPanelHidden = useSelector(selectors.ui.page.navPanelHidden);

  const [notifyAppMonitoringIsDisabled, setNotifyAppMonitoringIsDisabled] =
    useState<boolean>(false);
  const { data: customerSupportConfigurations } =
    useCustomerSupportConfigurations();
  const { shieldSettings, locationSecurityAppliesToAllDevices } =
    useSecurityPolicy();
  const [notifyEmployeeLoginIsDisabled, setNotifyEmployeeLoginIsDisabled] =
    useState<boolean>(false);
  const { data: locationCapabilitiesData } = useLocationCapabilities();
  const { data: employeeLoginDatabaseData } = useEmployeeLoginDatabase();
  const employeeLoginToggleIsEnabled =
    employeeLoginDatabaseData?.databaseData.enabled;
  useNetworkAccess();

  const employeeLoginFeatureIsSupported = useMemo(() => {
    const employeeLoginFeatureIsEnabledByCapability =
      !!customerSupportConfigurations?.customerFeatureEnablement
        .employeeOnboardingCaptivePortalEnabled &&
      !!locationCapabilitiesData?.state.capabilities?.captivePortalV2;
    const employeeLoginFeatureOverrideIsEnabled =
      !!customerSupportConfigurations?.customerFeatureEnablement
        .employeeOnboardingCaptivePortalWithoutCapabilityCheckEnabled;

    return (
      employeeLoginFeatureIsEnabledByCapability ||
      employeeLoginFeatureOverrideIsEnabled
    );
  }, [locationCapabilitiesData, customerSupportConfigurations]);

  useEffect(() => {
    if (
      customerSupportConfigurations?.customerFeatureEnablement &&
      locationSecurityAppliesToAllDevices.data.appTime !== undefined &&
      shieldSettings.data &&
      locationCapabilitiesData &&
      employeeLoginDatabaseData?.databaseData
    ) {
      const locationAppMonitoringIsTurnedOff = !shieldSettings.data.appTime;
      const deviceAppMonitoringExceptionsExist =
        !locationSecurityAppliesToAllDevices.data.appTime;

      if (
        locationAppMonitoringIsTurnedOff &&
        !deviceAppMonitoringExceptionsExist
      ) {
        setNotifyAppMonitoringIsDisabled(true);
      } else {
        setNotifyAppMonitoringIsDisabled(false);
      }

      if (employeeLoginFeatureIsSupported && !employeeLoginToggleIsEnabled) {
        setNotifyEmployeeLoginIsDisabled(true);
      } else {
        setNotifyEmployeeLoginIsDisabled(false);
      }
    }
  }, [
    customerSupportConfigurations,
    locationSecurityAppliesToAllDevices.data,
    shieldSettings,
    shieldSettings.data.appTime,
    notifyAppMonitoringIsDisabled,
    employeeLoginFeatureIsSupported,
    employeeLoginToggleIsEnabled,
  ]);

  const handleHamburgerClick = useCallback(() => {
    dispatch(actions.ui.page.setNavPanelHidden(!navPanelHidden));
    dispatch(actions.ui.page.setSidepanelOpen(false));
  }, [dispatch, navPanelHidden]);

  const handleLogoClick = useCallback(() => {
    dispatch(actions.ui.page.setSidepanelOpen(false));
    navigate(ROUTES.home.index);
  }, [navigate]);

  const { allDevices } = useDevices();
  const networkAccessData = useNetworkAccess();
  const { data: personsData, isLoading: personsIsLoading, errorMessage: personsErrorMessage } = useEmployees();
  const { allDevices: devicesData } = useDevices();

  const devicesIsLoading = devicesData.isLoading;
  const devicesErrorMessage = devicesData.errorMessage;

  const groupedPersons = useMemo(() => groupEmployeeList(personsData, devicesData.data), [personsData, devicesData.data]);

  const dataIsReady = !personsIsLoading && !devicesIsLoading && !personsErrorMessage && !devicesErrorMessage;
  const noDevicesCount = (dataIsReady && groupedPersons.noDevices.length) || 0;
  const noPrimaryDeviceCount = (dataIsReady && groupedPersons.noPrimaryDevice.length) || 0;

  const unapprovedSecureZoneDevicesCount = useMemo(() => {
    if (!networkAccessData?.data?.default?.purgatory) return 0;
    return (
      allDevices.data?.reduce(
        (count, dev) =>
          dev.networkAccessMode === 'auto' && dev.networkId === 'default'
            ? count + 1
            : count,
        0
      ) || 0
    );
  }, [allDevices.data, networkAccessData.data]);

  const unapprovedEmployeeZoneDevicesCount = useMemo(() => {
    if (!networkAccessData?.data?.employee?.purgatory) return 0;
    return (
      allDevices.data?.reduce(
        (count, dev) =>
          dev.networkAccessMode === 'auto' && dev.networkId === 'employee'
            ? count + 1
            : count,
        0
      ) || 0
    );
  }, [allDevices.data, networkAccessData.data]);

  const notifyBusinessProfileIsIncomplete = false; // TODO FES-6695: API integration

  return (
    <TopBarUI
      className={className}
      hamburgerOnClick={handleHamburgerClick}
      logoOnClick={handleLogoClick}
      noDevicesCount={noDevicesCount}
      noPrimaryDeviceCount={noPrimaryDeviceCount}
      unapprovedSecureZoneDevicesCount={unapprovedSecureZoneDevicesCount}
      unapprovedEmployeeZoneDevicesCount={unapprovedEmployeeZoneDevicesCount}
      withDebugMessage={debugEnabled}
      partnerId={activeLocation.data?.partnerId}
      showSearchAndNotifications={showSearchAndNotifications}
      notifyBusinessProfileIsIncomplete={notifyBusinessProfileIsIncomplete}
      notifyAppMonitoringIsDisabled={notifyAppMonitoringIsDisabled}
      notifyEmployeeLoginIsDisabled={notifyEmployeeLoginIsDisabled}
    />
  );
};
export default memo(TopBar);
